/**
 * @jsxRuntime classic
 * @jsx jsx
 * @jsxFrag jsx
 */

import { useEffect, useRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import Button, { IconButton } from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import VideoStopOverlayIcon from '@atlaskit/icon/core/video-stop-overlay';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';

import { RainbowProgressBar } from '../../primitives/rainbow-progress-bar/RainbowProgressBar';
import { RovoThinkingIndicator } from '../thinking-indicator/ThinkingIndicator';

import messages from './messages';
import { useProgressFacade } from './useProgressFacade';

// Rotating titles advance every 4s and stop on the last entry.
const ROTATION_INTERVAL_MS = 4000;

const rotatingMessages = defineMessages({
	rovoIsThinking: {
		id: 'ai-mate.inline-chat-input.thinking.rotating.rovo-is-thinking',
		defaultMessage: 'Rovo is thinking',
		description: 'Initial loading message shown while Rovo is starting to generate a response.',
	},
	analyzingContent: {
		id: 'ai-mate.inline-chat-input.thinking.rotating.analyzing-content',
		defaultMessage: 'Analyzing your content',
		description: 'Loading message shown while Rovo is analyzing the content.',
	},
	puttingPiecesTogether: {
		id: 'ai-mate.inline-chat-input.thinking.rotating.putting-pieces-together',
		defaultMessage: 'Putting the pieces together',
		description: 'Loading message shown while Rovo is assembling content.',
	},
	structuringResponse: {
		id: 'ai-mate.inline-chat-input.thinking.rotating.structuring-response',
		defaultMessage: 'Structuring your response',
		description: 'Loading message shown while Rovo is structuring the output.',
	},
	designingResponse: {
		id: 'ai-mate.inline-chat-input.thinking.rotating.designing-response',
		defaultMessage: 'Designing your response',
		description: 'Loading message shown while Rovo is designing the output.',
	},
	addingItAllTogether: {
		id: 'ai-mate.inline-chat-input.thinking.rotating.adding-it-all-together',
		defaultMessage: 'Adding it all together',
		description: 'Loading message shown while Rovo is combining all elements.',
	},
	applyingFinalTouches: {
		id: 'ai-mate.inline-chat-input.thinking.rotating.applying-final-touches',
		defaultMessage: 'Applying the final touches',
		description: 'Loading message shown while Rovo is finalizing.',
	},
	almostDone: {
		id: 'ai-mate.inline-chat-input.thinking.rotating.almost-done',
		defaultMessage: 'Almost done',
		description: 'Loading message shown when Rovo is nearly finished.',
	},
});

/** Cycles through `texts` at a fixed interval, stopping on the last item. */
function useSequentialText(texts: string[], enabled: boolean): string {
	const [index, setIndex] = useState(0);
	const indexRef = useRef(0);
	const textsRef = useRef(texts);
	textsRef.current = texts;

	useEffect(() => {
		if (!enabled || textsRef.current.length <= 1) {
			return;
		}
		const interval = setInterval(() => {
			const next = indexRef.current + 1;
			if (next < textsRef.current.length) {
				indexRef.current = next;
				setIndex(next);
			} else {
				clearInterval(interval);
			}
		}, ROTATION_INTERVAL_MS);
		return () => clearInterval(interval);
	}, [enabled]);

	return texts[index] ?? texts[texts.length - 1] ?? '';
}

const styles = cssMap({
	escape: {
		paddingInline: token('space.050'),
		paddingBlock: token('space.025'),
		backgroundColor: token('color.background.accent.gray.subtlest'),
		color: token('color.text.subtlest'),
		borderRadius: token('radius.small'),
		font: token('font.body.small'),
	},

	container: {
		display: 'flex',
		flexDirection: 'row',
		alignItems: 'center',
		gap: token('space.100'),
		width: '100%',
	},

	actions: {
		marginLeft: 'auto',
	},

	indicatorWrapper: {
		flex: '1',
		minWidth: 0,
		overflow: 'hidden',
	},
});

export type RovoThinkingBarStatus = 'analyzing' | 'editing';

export type RovoThinkingBarProps = {
	status?: RovoThinkingBarStatus;
	onStop: () => void;
	onFollow?: () => void;
	showEscapeHint: boolean;
	useShortMessages?: boolean;
	/**
	 * When true, renders a RainbowProgressBar below the thinking indicator.
	 */
	showProgressBar?: boolean;
	/**
	 * Signals the progress bar to rapidly fill to 100%.
	 * Only relevant when `showProgressBar` is true.
	 */
	isComplete?: boolean;
	/**
	 * Called after the progress bar finish-to-100% animation completes.
	 * Only relevant when `showProgressBar` is true.
	 */
	onCompleteAnimationFinished?: () => void;
	/** When true, derives the indicator title from a rotating list of messages. */
	useRotatingTitle?: boolean;
};

/**
 * Inner content of the Rovo thinking bar — thinking indicator + action buttons.
 *
 * Extracted from ThinkingView in conversation-assistant-chat-prompt-input so it
 * can be reused in contexts outside of the inline chat prompt flow (e.g. BOE
 * whiteboard and database streaming experiences).
 */
export const RovoThinkingBar = ({
	status = 'analyzing',
	onStop,
	onFollow,
	showEscapeHint,
	useShortMessages = false,
	showProgressBar = false,
	isComplete,
	onCompleteAnimationFinished,
	useRotatingTitle = false,
}: RovoThinkingBarProps): JSX.Element => {
	const { formatMessage } = useIntl();

	const progress = useProgressFacade({
		isComplete,
		onCompleteAnimationFinished,
	});

	const staticTitle = formatMessage(
		status === 'editing'
			? useShortMessages
				? messages.editingTextShort
				: messages.editingText
			: useShortMessages
				? messages.thinkingTextShort
				: messages.thinkingText,
	);

	const rotatingEnabled = useRotatingTitle && !useShortMessages;
	const rotatingTitles = [
		formatMessage(rotatingMessages.rovoIsThinking),
		formatMessage(rotatingMessages.analyzingContent),
		formatMessage(rotatingMessages.puttingPiecesTogether),
		formatMessage(rotatingMessages.structuringResponse),
		formatMessage(rotatingMessages.designingResponse),
		formatMessage(rotatingMessages.addingItAllTogether),
		formatMessage(rotatingMessages.applyingFinalTouches),
		formatMessage(rotatingMessages.almostDone),
	];
	const rotatingTitle = useSequentialText(rotatingTitles, rotatingEnabled);

	const title = rotatingEnabled ? rotatingTitle : staticTitle;

	const actionsNode = (
		<Inline
			testId="inline-chat__thinking-view"
			alignBlock="center"
			space="space.100"
			xcss={
				expValEquals('platform_editor_comment_rovoinlinechat_improvement', 'isEnabled', true)
					? styles.actions
					: undefined
			}
		>
			{onFollow && (
				<Button appearance="subtle" onClick={onFollow}>
					{formatMessage(messages.followAiMateButton)}
				</Button>
			)}
			{!fg('update_remix_and_ask_rovo_thinking_bars_styles') && showEscapeHint && (
				<Box as="span" xcss={styles.escape}>
					{formatMessage(messages.escapeKeyHint)}
				</Box>
			)}
			<IconButton
				isTooltipDisabled={false}
				shape={fg('update_remix_and_ask_rovo_thinking_bars_styles') ? 'default' : 'circle'}
				onClick={onStop}
				icon={(iconProps) => (
					<VideoStopOverlayIcon {...iconProps} color={token('color.icon.danger')} label={''} />
				)}
				label={formatMessage(messages.stopGeneratingButtonLabel)}
			/>
		</Inline>
	);

	return (
		<Box xcss={styles.container}>
			<Box xcss={styles.indicatorWrapper}>
				<RovoThinkingIndicator
					title={title}
					animation={fg('update_remix_and_ask_rovo_thinking_bars_styles')}
					bottomAdornment={
						showProgressBar ? (
							<RainbowProgressBar
								testId="rovo-thinking-progress-bar"
								progress={progress}
								appearance="static-rainbow"
								label={title}
							/>
						) : undefined
					}
				/>
			</Box>
			{actionsNode}
		</Box>
	);
};
